import type {
  TInProgressMajorActionData,
  TLastRentThisVisit,
} from '@kapitalist/contract/types'
import * as NEL from '@kapitalist/utils/NEList'
import {DataBackedModel} from '~/models/base'
import {
  majorActionFactory,
  minorActionFactory,
  minorActionPromptFactory,
} from '~/models/game/factories'
import type GamePlayer from '~/models/game/GamePlayer'
import type {
  AnyMinorAction,
  AnyMinorActionPrompt,
  MajorActionAllowsMinorActions,
} from '~/models/game/types'

export default class InProgressMajorAction extends DataBackedModel<TInProgressMajorActionData> {
  majorAction: MajorActionAllowsMinorActions
  pendingMinorActions: NEL.NEList<AnyMinorActionPrompt>

  // server orders these in most-recent-first order.
  // having deterministic order is important to the JSN code, at least.
  previousMinorActions: AnyMinorAction[]
  majorActor: GamePlayer

  constructor(
    data: TInProgressMajorActionData,
    majorActor: GamePlayer,
    players: GamePlayer[],
    lastRentThisVisit: TLastRentThisVisit | undefined
  ) {
    super(data)
    this.majorActor = majorActor
    this.majorAction = majorActionFactory(
      data.majorActionData,
      majorActor.portfolio,
      {
        lastRentThisVisit,
      }
    )

    // this NEL is nice to enforce for now. I'm not sure if e.g. pastGamePositions
    // will violate this constraint or not yet.
    const pendingMinorActions = NEL.createSafe(
      data.pendingMinorActions.map(pma => {
        const minorActor = players.find(p => p.id === pma.minorActorPlayerId)
        if (!minorActor) {
          throw new Error(
            `minor actor not found in player list when constructing InProgressMajorAction: ${JSON.stringify(pma)}`
          )
        }
        return minorActionPromptFactory(pma, this.majorAction, {
          majorActor,
          minorActor,
        })
      })
    )

    if (!pendingMinorActions) {
      // this happened once when accepting a forced deal, where the forced deal created a complete set for the attacked
      // player. I couldn't figure out from the data why it happened, so I added more logging here just in case
      console.error({
        data,
        majorActor,
        players,
        lastRentThisVisit,
        pendingMinorActions,
        majorAction: this.majorAction,
      })
      throw new Error(`InProgressMajorAction has no pendingMinorActions`)
    }
    this.pendingMinorActions = pendingMinorActions

    // will probably want to add major/minor actor to this constructor too like above
    this.previousMinorActions = data.previousMinorActions.map(minorActionFactory)
  }
}
